import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { ContainerState, PaginationData, PaginationRange } from 'tiime-material';

import { AuditApiService } from '@api-services/audit-api.service';
import { ApiTableDataStoreBase, ApiTableDataStoreState } from '@bases/table-data-store/api-table-data-store.base';
import { AuditEntityType } from '@enums/audit-entity-type.enum';
import { Audit } from '@models/audit';

export interface AuditsFilters {
  entityType: AuditEntityType;
  entityId: number;
  entityProperties: string[];
}

export type AuditsStoreState = ApiTableDataStoreState<Audit, AuditsFilters>;

export const initialState: AuditsStoreState = {
  data: [],
  containerState: ContainerState.contentPlaceholder,
  pagination: PaginationRange.withPageSize(100),
  filters: {
    entityType: null,
    entityId: null,
    entityProperties: null
  },
  search: null,
  sort: null,
  isLoading: false,
  selected: null
};

@Injectable()
export class AuditsStore extends ApiTableDataStoreBase<Audit, AuditsFilters, AuditsStoreState> {
  constructor(private auditApiService: AuditApiService) {
    super();
  }

  protected override getData(): Observable<Audit[]> {
    const { pagination, filters } = this.get();

    return this.auditApiService
      .getAudits(filters.entityType, filters.entityId, pagination, filters.entityProperties)
      .pipe(
        tap((paginationData: PaginationData<Audit>) => {
          const hasData = paginationData.data.length > 0;
          const shouldOnBoard = paginationData.paginationRange.min === 0;

          let containerState: ContainerState = ContainerState.noResult;
          if (hasData) {
            containerState = ContainerState.done;
          } else if (shouldOnBoard) {
            containerState = ContainerState.onboard;
          }

          this.patchState({
            data: paginationData.data,
            pagination: paginationData.paginationRange,
            containerState,
            isLoading: false
          });
        }),
        map(({ data }) => data),
        catchError(() => of([]))
      );
  }
}
